import React, { FC, Fragment } from 'react';
import _ from 'lodash';
import { useIntl } from 'react-intl';
import { useHistory } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { AlertType, appendAlertItem, AsyncCsvLink } from '@pruforce/common-adminweb-sdk';
import PruTable from '../../../../../../../common/components/PruTable/PruTable';
import { getDefaultDisplayDate, isPermitted, useJWT } from 'src/app/common/utils';
import { PaginateList } from '../../../../../../../common/types/common-types';
import { uploadPath } from '../../BulkUploadStatusRoutes';
import { BULK_UPLOAD_STATUS_TYPE } from 'src/app/modules/pruwallet/types/bulk-upload-types';
import { AdminWalletPermissionType } from 'src/app/modules/pruwallet/constants';
import { fetchBlobDetails, getError } from 'src/app/modules/pruwallet/comm';

type BulkUploadStatusListProps = {
  isLoading: boolean;
  bulkUploadStatusList?: PaginateList<BulkUploadStatusItem>;
  onRefresh: () => void;
  onChangePage: (page: number, rowsPerPage: number) => void;
  onApprove: (id: string, sourceService: string) => void;
};

export type BulkUploadStatusItem = {
  id: string;
  failedRecords: number;
  fileName: string;
  operationType: string;
  processedRecords: number;
  status: string;
  totalRecords: number;
  rowFailed: number;
  createdAt: string;
  blobId: string;
  approvedAt?: string;
  sourceService: string;
};

enum BULK_UPLOAD_STATUS {
  PENDING = 'PENDING',
  PROCESSING = 'PROCESSING',
  COMPLETED = 'COMPLETED',
  FAILED = 'FAILED',
  CANCELLED = 'CANCELLED',
}

const BulkUploadStatusList: FC<BulkUploadStatusListProps> = ({
  isLoading,
  bulkUploadStatusList,
  onRefresh,
  onChangePage,
  onApprove,
}) => {
  const history = useHistory();
  const dispatch = useDispatch();
  const intl = useIntl();
  const Translation = (id: string) => intl.formatMessage({ id });

  let approvePermission = false;
  const jwt = useJWT() || {};
  if (jwt.permissions) {
    const jwtPermission = _.map(jwt.permissions, (_, key) => key);
    approvePermission = isPermitted([AdminWalletPermissionType.WALLET_BULKUPLOAD_STATUS_APPROVE], jwtPermission);
  }
  const getOperationTypeTranslation = (operationType?: string): string => {
    if (!operationType) return '-';
    const enumKey = operationType as keyof typeof BULK_UPLOAD_STATUS_TYPE;
    const mappedValue = BULK_UPLOAD_STATUS_TYPE[enumKey];
    return mappedValue ? Translation(`title.wallet.bulkUpload.upload.${mappedValue}`) : '-';
  };

  const onPreviewCsv = async (blobId: string, fileName: string) => {
    if (!blobId) {
      dispatch(appendAlertItem([{ severity: AlertType.ERROR, title: 'Error', content: 'Blob ID is missing' }]));
      return;
    }

    try {
      const blobDetail = await fetchBlobDetails(blobId, dispatch);
      if (!blobDetail?.url) throw new Error('Preview url is empty');

      const res = await fetch(blobDetail.url, { method: 'GET' });
      if (!res.ok) throw new Error(`Download failed: ${res.status}`);
      const blob = await res.blob();
      const objectUrl = URL.createObjectURL(blob);

      const a = document.createElement('a');
      a.href = objectUrl;
      (a.download = `${fileName}`), (a.rel = 'noopener noreferrer');
      a.style.display = 'none';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(objectUrl);
    } catch (err) {
      dispatch(
        appendAlertItem([
          {
            severity: AlertType.ERROR,
            title: 'Error',
            content: `${getError(err)?.message || err}`,
          },
        ]),
      );
    }
  };

  return (
    <Fragment>
      <PruTable
        title={Translation('wallet.bulkUpload.list')}
        disableBulkSelect
        operationDef={[]}
        columnDef={[
          {
            isId: true,
            hidden: true,
            keyIndex: 'id',
            displayName: '',
            renderData: () => '',
          },
          {
            keyIndex: 'fileName',
            displayName: Translation('wallet.bulkUpload.common.filename'),
            renderData: (row) =>
              row.fileName ? (
                <button
                  style={{ cursor: 'pointer', color: 'blue', textDecoration: 'underline' }}
                  onClick={() => {
                    onPreviewCsv(row.blobId, row.fileName);
                  }}
                >
                  {row.fileName}
                </button>
              ) : (
                '-'
              ),
          },
          {
            keyIndex: 'operationType',
            align: 'center',
            displayName: Translation('wallet.bulkUpload.common.bulkUploadType'),
            renderData: (row) => getOperationTypeTranslation(row.operationType),
          },
          {
            keyIndex: 'status',
            align: 'center',
            displayName: Translation('wallet.bulkUpload.common.status'),
            renderData: (row) => row.status || '-',
          },
          {
            keyIndex: 'approve',
            align: 'center',
            displayName: Translation('wallet.bulkUpload.common.approve'),
            renderData: (row) => {
              return approvePermission ? (
                <div
                  style={
                    row.status === BULK_UPLOAD_STATUS.PENDING
                      ? { cursor: 'pointer', color: 'blue', textDecoration: 'underline' }
                      : undefined
                  }
                  onClick={() => {
                    if (row.status !== BULK_UPLOAD_STATUS.PENDING) return;
                    onApprove(row.id, row.sourceService);
                  }}
                >
                  {row.status === BULK_UPLOAD_STATUS.PENDING
                    ? Translation('wallet.bulkUpload.common.approve')
                    : Translation('wallet.bulkUpload.common.approved')}
                </div>
              ) : (
                <>
                  {row.status === BULK_UPLOAD_STATUS.PENDING
                    ? Translation('wallet.bulkUpload.common.pending')
                    : Translation('wallet.bulkUpload.common.approved')}
                </>
              );
            },
          },
          {
            keyIndex: 'approvedAt',
            align: 'center',
            displayName: Translation('wallet.bulkUpload.common.approvedAt'),
            renderData: (row) => getDefaultDisplayDate(row.approvedAt ? new Date(row.approvedAt) : null, 'datetime'),
          },
          {
            keyIndex: 'totalRecord',
            align: 'center',
            displayName: Translation('wallet.bulkUpload.common.totalRecords'),
            renderData: (row) => row.totalRecords.toString() || '-',
          },
          {
            keyIndex: 'processedResscords',
            align: 'center',
            displayName: Translation('wallet.bulkUpload.common.processedResscords'),
            renderData: (row) => row.processedRecords.toString() || '-',
          },
          {
            keyIndex: 'failedRecords',
            align: 'center',
            displayName: 'Failed Records',
            renderData: (row) =>
              row.failedRecords > 0 ? (
                <a
                  style={{ color: 'red' }}
                  onClick={() => history.push(`${uploadPath}/${row.id}/failed/list?type=${row.operationType}`)}
                >
                  {row.failedRecords.toString()}
                </a>
              ) : (
                row.failedRecords.toString()
              ),
          },
          {
            keyIndex: 'startedAt',
            align: 'center',
            displayName: Translation('wallet.bulkUpload.common.updatedAt'),
            renderData: (row) => getDefaultDisplayDate(new Date(row.createdAt), 'datetime'),
          },
        ]}
        isLoading={isLoading}
        onRefresh={onRefresh}
        dataSource={bulkUploadStatusList?.docs}
        totalPages={bulkUploadStatusList?.totalPages}
        totalRecords={bulkUploadStatusList?.totalDocs}
        onChangePage={onChangePage}
      />
    </Fragment>
  );
};

export default BulkUploadStatusList;
